// Shared caching utility for platform-agnostic caching
// Provides basic TTL-based caching without platform-specific dependencies

export interface CacheItem<T = unknown> {
  data: T;
  timestamp: number;
  ttl: number;
}

export interface CacheConfig {
  defaultTTL: number;
  maxSize: number;
}

export interface CacheStats {
  size: number;
  maxSize: number;
  hitRate?: number;
  totalRequests?: number;
  cacheHits?: number;
  misses?: number;
  evictions?: number;
}

export interface CacheKeyParams {
  userId: string;
  tool: string;
  parameters?: Record<string, unknown>;
}

export interface EnhancedCacheItem<T = unknown> extends CacheItem<T> {
  accessCount: number;
  lastAccessed: number;
}

/**
 * Base cache class providing core caching functionality
 * Platform-agnostic and can be extended for specific storage mechanisms
 */
export class BaseCache<T = unknown> {
  protected cache = new Map<string, CacheItem<T>>();
  protected config: CacheConfig;
  protected stats = {
    totalRequests: 0,
    cacheHits: 0,
    misses: 0,
    evictions: 0,
  };

  constructor(config: Partial<CacheConfig> = {}) {
    this.config = {
      defaultTTL: 5 * 60 * 1000, // 5 minutes default
      maxSize: 100,
      ...config,
    };
  }

  /**
   * Set an item in the cache
   */
  set(key: string, data: T, ttl?: number): void {
    const item: CacheItem<T> = {
      data,
      timestamp: Date.now(),
      ttl: ttl || this.config.defaultTTL,
    };

    // Implement LRU eviction if cache is full
    if (this.cache.size >= this.config.maxSize && !this.cache.has(key)) {
      const firstKey = this.cache.keys().next().value;
      if (firstKey) {
        this.cache.delete(firstKey);
        this.stats.evictions++;
      }
    }

    this.cache.set(key, item);
  }

  /**
   * Get an item from the cache
   */
  get(key: string): T | null {
    this.stats.totalRequests++;

    const item = this.cache.get(key);
    if (!item) {
      this.stats.misses++;
      return null;
    }

    // Check if item has expired
    if (Date.now() - item.timestamp > item.ttl) {
      this.cache.delete(key);
      this.stats.misses++;
      return null;
    }

    this.stats.cacheHits++;
    return item.data;
  }

  /**
   * Check if an item exists and is valid
   */
  has(key: string): boolean {
    return this.get(key) !== null;
  }

  /**
   * Delete an item from the cache
   */
  delete(key: string): boolean {
    return this.cache.delete(key);
  }

  /**
   * Clear all items from the cache
   */
  clear(): void {
    this.cache.clear();
    this.stats.totalRequests = 0;
    this.stats.cacheHits = 0;
    this.stats.misses = 0;
    this.stats.evictions = 0;
  }

  /**
   * Get cache statistics
   */
  getStats(): CacheStats {
    const hitRate =
      this.stats.totalRequests > 0
        ? (this.stats.cacheHits / this.stats.totalRequests) * 100
        : 0;

    return {
      size: this.cache.size,
      maxSize: this.config.maxSize,
      hitRate,
      totalRequests: this.stats.totalRequests,
      cacheHits: this.stats.cacheHits,
      misses: this.stats.misses,
      evictions: this.stats.evictions,
    };
  }

  /**
   * Clean up expired items
   */
  cleanup(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];

    for (const [key, item] of this.cache.entries()) {
      if (now - item.timestamp > item.ttl) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * Get all cache keys
   */
  keys(): string[] {
    return Array.from(this.cache.keys());
  }

  /**
   * Get cache size
   */
  get size(): number {
    return this.cache.size;
  }
}

/**
 * In-memory cache implementation
 * Suitable for most use cases where persistence isn't required
 */
export class MemoryCache<T = unknown> extends BaseCache<T> {
  constructor(config?: Partial<CacheConfig>) {
    super(config);
  }
}

/**
 * Cache TTL constants (in milliseconds)
 */
export const CACHE_TTL = {
  SHORT: 1 * 60 * 1000, // 1 minute
  MEDIUM: 5 * 60 * 1000, // 5 minutes
  LONG: 30 * 60 * 1000, // 30 minutes
  VERY_LONG: 24 * 60 * 60 * 1000, // 24 hours
} as const;

/**
 * Utility functions for common caching patterns
 */
export class CacheUtils {
  /**
   * Create a cache key from multiple parameters
   */
  static createKey(...params: (string | number | boolean)[]): string {
    return params.map(param => String(param)).join(':');
  }

  /**
   * Check if data is stale based on timestamp and max age
   */
  static isStale(timestamp: number, maxAge: number): boolean {
    return Date.now() - timestamp > maxAge;
  }

  /**
   * Calculate remaining TTL for an item
   */
  static getRemainingTTL(timestamp: number, ttl: number): number {
    const elapsed = Date.now() - timestamp;
    return Math.max(0, ttl - elapsed);
  }
}

/**
 * Enhanced cache with performance monitoring and advanced features
 */
export class EnhancedCache<T = unknown> extends BaseCache<T> {
  protected cache = new Map<string, EnhancedCacheItem<T>>();

  constructor(config?: Partial<CacheConfig>) {
    super(config);
  }

  /**
   * Generate cache key based on user ID and request parameters
   */
  generateCacheKey(params: CacheKeyParams): string {
    const { userId, tool, parameters = {} } = params;

    // Sort parameters to ensure consistent key generation
    const sortedParams = Object.keys(parameters)
      .sort()
      .reduce(
        (result, key) => {
          result[key] = parameters[key];
          return result;
        },
        {} as Record<string, unknown>
      );

    const paramString = JSON.stringify(sortedParams);
    return `${userId}:${tool}:${Buffer.from(paramString).toString('base64')}`;
  }

  /**
   * Enhanced set with access tracking
   */
  set(key: string, data: T, ttl?: number): void {
    const now = Date.now();
    const item: EnhancedCacheItem<T> = {
      data,
      timestamp: now,
      ttl: ttl || this.config.defaultTTL,
      accessCount: 0,
      lastAccessed: now,
    };

    // Implement LRU eviction if cache is full
    if (this.cache.size >= this.config.maxSize && !this.cache.has(key)) {
      this.evictLeastRecentlyUsed();
    }

    this.cache.set(key, item);
  }

  /**
   * Enhanced get with access tracking
   */
  get(key: string): T | null {
    this.stats.totalRequests++;

    const item = this.cache.get(key) as EnhancedCacheItem<T> | undefined;
    if (!item) {
      this.stats.misses++;
      return null;
    }

    const now = Date.now();

    // Check if item has expired
    if (now - item.timestamp > item.ttl) {
      this.cache.delete(key);
      this.stats.misses++;
      return null;
    }

    // Update access statistics
    item.accessCount++;
    item.lastAccessed = now;

    this.stats.cacheHits++;
    return item.data;
  }

  /**
   * Invalidate cache entries by pattern
   */
  invalidateByPattern(pattern: string): number {
    let invalidated = 0;
    const regex = new RegExp(pattern);

    for (const key of this.cache.keys()) {
      if (regex.test(key)) {
        this.cache.delete(key);
        invalidated++;
      }
    }

    return invalidated;
  }

  /**
   * Invalidate all cache entries for a specific user
   */
  invalidateUserCache(userId: string): number {
    return this.invalidateByPattern(`^${userId}:`);
  }

  /**
   * Invalidate cache entries for a specific tool
   */
  invalidateToolCache(tool: string): number {
    return this.invalidateByPattern(`:${tool}:`);
  }

  /**
   * Get detailed cache statistics
   */
  getDetailedStats(): {
    stats: CacheStats;
    entries: Array<{
      key: string;
      age: number;
      ttl: number;
      accessCount: number;
      lastAccessed: number;
    }>;
  } {
    const now = Date.now();
    const entries = Array.from(this.cache.entries()).map(([key, item]) => ({
      key,
      age: now - item.timestamp,
      ttl: item.ttl,
      accessCount: (item as EnhancedCacheItem<T>).accessCount || 0,
      lastAccessed:
        (item as EnhancedCacheItem<T>).lastAccessed || item.timestamp,
    }));

    return {
      stats: this.getStats(),
      entries,
    };
  }

  /**
   * Evict least recently used entry to make room
   */
  private evictLeastRecentlyUsed(): void {
    let oldestKey: string | null = null;
    let oldestTime = Number.MAX_SAFE_INTEGER;

    for (const [key, item] of this.cache.entries()) {
      const enhancedItem = item as EnhancedCacheItem<T>;
      const lastAccessed = enhancedItem.lastAccessed || enhancedItem.timestamp;

      if (lastAccessed < oldestTime) {
        oldestTime = lastAccessed;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      this.cache.delete(oldestKey);
      this.stats.evictions++;
    }
  }

  /**
   * Start periodic cleanup of expired entries
   */
  startPeriodicCleanup(intervalMs: number = 60000): NodeJS.Timeout {
    return setInterval(() => {
      this.cleanup();
    }, intervalMs);
  }
}

/**
 * Cache invalidation helper for data updates
 */
export class CacheInvalidator {
  constructor(private cache: EnhancedCache) {}

  /**
   * Invalidate cache when user data changes
   */
  invalidateUserData(userId: string): void {
    const invalidated = this.cache.invalidateUserCache(userId);
    console.log('User cache invalidated', { userId, invalidated });
  }

  /**
   * Invalidate cache for specific tool
   */
  invalidateToolData(toolName: string): void {
    const invalidated = this.cache.invalidateToolCache(toolName);
    console.log('Tool cache invalidated', { tool: toolName, invalidated });
  }

  /**
   * Invalidate cache by custom pattern
   */
  invalidateByPattern(pattern: string): void {
    const invalidated = this.cache.invalidateByPattern(pattern);
    console.log('Cache invalidated by pattern', { pattern, invalidated });
  }

  /**
   * Invalidate portfolio-related caches when portfolio data changes
   */
  invalidatePortfolioData(userId: string): void {
    const patterns = [
      `${userId}:get_portfolio_summary:`,
      `${userId}:get_holdings_allocation:`,
      `${userId}:get_accounts_data:`,
    ];

    let totalInvalidated = 0;
    patterns.forEach(pattern => {
      totalInvalidated += this.cache.invalidateByPattern(pattern);
    });

    console.log('Portfolio cache invalidated', {
      userId,
      invalidated: totalInvalidated,
    });
  }

  /**
   * Invalidate goals-related caches when goals data changes
   */
  invalidateGoalsData(userId: string): void {
    const invalidated = this.cache.invalidateByPattern(
      `${userId}:get_goals_data:`
    );
    console.log('Goals cache invalidated', { userId, invalidated });
  }

  /**
   * Invalidate ideas-related caches when ideas data changes
   */
  invalidateIdeasData(userId: string): void {
    const invalidated = this.cache.invalidateByPattern(
      `${userId}:get_ideas_data:`
    );
    console.log('Ideas cache invalidated', { userId, invalidated });
  }
}

/**
 * Cache health check utility
 */
export function getCacheHealth(cache: EnhancedCache): {
  status: 'healthy' | 'degraded' | 'unhealthy';
  metrics: CacheStats;
  issues: string[];
} {
  const metrics = cache.getStats();
  const issues: string[] = [];

  // Check hit rate
  if ((metrics.hitRate || 0) < 50 && (metrics.totalRequests || 0) > 100) {
    issues.push('Low cache hit rate');
  }

  // Check cache size
  if (metrics.size > metrics.maxSize * 0.9) {
    issues.push('Cache near capacity');
  }

  // Check eviction rate
  if ((metrics.evictions || 0) > (metrics.cacheHits || 0) * 0.1) {
    issues.push('High eviction rate');
  }

  let status: 'healthy' | 'degraded' | 'unhealthy' = 'healthy';
  if (issues.length > 0) {
    status = issues.length >= 2 ? 'unhealthy' : 'degraded';
  }

  return {
    status,
    metrics,
    issues,
  };
}

// Export default enhanced cache instance for convenience
export const cache = new EnhancedCache({
  defaultTTL: 5 * 60 * 1000, // 5 minutes
  maxSize: 1000,
});

// Export cache invalidator instance
export const cacheInvalidator = new CacheInvalidator(cache);
